import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import * as FileSaver from 'file-saver';
import * as moment from 'moment';
import { tap } from 'rxjs/operators';

import { CompanySettings } from '../settings/model/company-settings';
import * as fromCompanySettingsStore from '../settings/store';
import { BrowserDetectionService } from './browser/browser-detection.service';
import { DownloadExceptionModalComponent } from './components/modal/download-exception-modal/download-exception-modal.component';
import { ModalService } from './components/modal/modal.service';
import { Version } from './version/version';
import NumberFormat = Intl.NumberFormat;

@Injectable()
export class CsvExporterService {
  private minimumSafariVersion: Version = new Version(10, 2, 0);
  private numberFormatter: NumberFormat;

  constructor(
    private modal: ModalService,
    private browser: BrowserDetectionService,
    private store: Store<fromCompanySettingsStore.CompanyState>
  ) {
    if (this.browser.hasNativeIntl()) {
      fromCompanySettingsStore
        .getSettingsFromStore(this.store)
        .pipe(
          tap((settings: CompanySettings) => {
            this.numberFormatter = this.createNumberFormatter(settings.locale);
          })
        )
        .subscribe();
    }
  }

  public exportData(
    filename: string,
    items: Array<any>,
    headerConfig: Array<{ field: string | Function; header: string }>,
    separator: string = ','
  ): void {
    let csv: string = this.createCsv(items, headerConfig, separator);
    if (
      this.browser.isSafari() &&
      this.minimumSafariVersion.isGreaterThen(this.browser.getSafariVersion())
    ) {
      csv = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
      this.modal.open(DownloadExceptionModalComponent, {
        safeUrl: csv,
        filename: filename
      });
    } else {
      const blob: Blob = new Blob([csv], { type: 'text/csv' });
      FileSaver.saveAs(blob, filename);
    }
  }

  private createCsv(
    items: Array<any>,
    headerConfig: Array<{ field: string | Function; header: string }>,
    separator: string = ','
  ): string {
    if (
      this.browser.hasNativeIntl() &&
      this.numberFormatter.format(1.5).indexOf(',') > -1 &&
      separator === ','
    ) {
      separator = ';';
    }
    const headers: Array<string> = [];
    const fields: Array<string | Function> = headerConfig.map(
      (item: { field: string | Function; header: string }) => {
        headers.push(`"${item.header}"`);
        return item.field;
      }
    );

    const csv: string = [headers.join(separator)]
      .concat(items.map((row: any) => this.mapRow(row, fields, separator)))
      .join('\r\n');

    return csv;
  }

  private mapRow(
    row: any,
    fields: Array<string | Function>,
    separator: string
  ): string {
    return fields
      .map((field: string) => {
        if (typeof field === 'function') {
          return this.mapField(field, row);
        } else {
          return this.mapField(row[field]);
        }
      })
      .join(separator);
  }

  private mapField(field: any, item?: any): string {
    switch (typeof field) {
      case 'string':
        return `"${field}"`;
      case 'number':
        if (!this.browser.hasNativeIntl()) {
          return field.toString();
        } else {
          return this.numberFormatter.format(field);
        }
      case 'function':
        return field(item);
      case 'object':
        return this.mapObject(field);
      default:
        return field.toString();
    }
  }

  private mapObject(object: any): string {
    if (object === null || object === undefined) {
      return '';
    } else if (object instanceof Date || moment.isMoment(object)) {
      return `"${moment(object).format('L LT')}"`;
    } else {
      return `"${object.toString()}"`;
    }
  }

  private createNumberFormatter(locale: string): NumberFormat {
    return new Intl.NumberFormat(locale, {
      useGrouping: false
    });
  }
}
